import { msg } from "@lingui/macro"
import { useLingui } from "@lingui/react"
import { useState } from 'react'
import {
    Box,
    Button,
    Divider,
    Drawer,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import EChart from '../data-analysis/charts/EChart'

export default function PolygonStatsResults({
    open,
    isMobile,
    onClose,
    chartData,
    barChartConfig,
    pieChartConfig,
    onChartTypeChange,
    secondChartOptions = [],
    onSecondChartSearch,
    secondChartData,
    secondChartConfig,
    severitySummary = null,
    width
}) {
    const theme = useTheme()
    const { _ } = useLingui()
    const textColor = theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white'
    const isLight = theme.palette.mode === 'light'
    const paperBg = isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.06)'
    const borderColor = isLight ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.15)'
    const [selectedSecondIndex, setSelectedSecondIndex] = useState(0)

    return (
        <Drawer
            variant="persistent"
            anchor="right"
            open={open}
            sx={{
                width: isMobile ? '100%' : width,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: isMobile ? '100%' : width,
                    height: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
            }}
        >
            <Box sx={{ py: 1.5, px: 2, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <Typography variant="h6" sx={{ color: textColor, fontWeight: 700, fontSize: 18 }}>
                        {_(msg`Statistics result`)}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={onClose}
                        sx={{ color: textColor, '&:hover': { backgroundColor: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)' } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Divider sx={{ borderColor, marginTop:'6px',marginBottom:'6px', flexShrink: 0 }} />
                <Paper
                    variant="outlined"
                    sx={{
                        p: 1.5,
                        mb: 1.5,
                        flexShrink: 0,
                        backgroundColor: paperBg,
                        borderColor,
                        borderRadius: 1.5,
                    }}
                >
                    <Typography variant="subtitle2" sx={{ color: textColor, fontWeight: 600, mb: 0.5, fontSize: '0.8rem' }}>
                        {_(msg`Summary`)}
                    </Typography>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { color: textColor, borderColor, py: 0.5, fontSize: '0.8rem' } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>{_(msg`Metric`)}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>{_(msg`Count`)}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {severitySummary ? (
                                <>
                                    <TableRow>
                                        <TableCell>{_(msg`Total`)}</TableCell>
                                        <TableCell align="right">{severitySummary.total ?? '—'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{_(msg`Unknown`)}</TableCell>
                                        <TableCell align="right">{severitySummary.unknownSeverity ?? '—'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{_(msg`No injury`)}</TableCell>
                                        <TableCell align="right">{severitySummary.noInjurySeverity ?? '—'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{_(msg`Slight injury`)}</TableCell>
                                        <TableCell align="right">{severitySummary.slightInjurySeverity ?? '—'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{_(msg`Serious injury`)}</TableCell>
                                        <TableCell align="right">{severitySummary.seriousInjurySeverity ?? '—'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{_(msg`Fatal`)}</TableCell>
                                        <TableCell align="right">{severitySummary.fatalSeverity ?? '—'}</TableCell>
                                    </TableRow>
                                </>
                            ) : (
                                <>
                                    <TableRow><TableCell>—</TableCell><TableCell align="right">—</TableCell></TableRow>
                                    <TableRow><TableCell>—</TableCell><TableCell align="right">—</TableCell></TableRow>
                                </>
                            )}
                        </TableBody>
                    </Table>
                </Paper>
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {!chartData?.length ? (
                        <Typography sx={{ py: 3, color: textColor, textAlign: 'center' }}>
                            {_(msg`No data`)}
                        </Typography>
                    ) : (
                        <>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 1.5,
                                    flex: 1,
                                    minHeight: 360,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    backgroundColor: paperBg,
                                    borderColor,
                                    borderRadius: 1.5,
                                    overflow: 'hidden',
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ color: textColor, fontWeight: 600, mb: 0.5, fontSize: '0.8rem', flexShrink: 0 }}>
                                    {_(msg`Crash Severity`)}
                                </Typography>
                                <Box sx={{ flex: 1, minHeight: 280, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                    <EChart chart={barChartConfig} index={0} handleCharts={onChartTypeChange} width="100%" height="100%" areasGeoJson={null} />
                                </Box>
                            </Paper>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 1.5,
                                    flex: 1,
                                    minHeight: 360,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    backgroundColor: paperBg,
                                    borderColor,
                                    borderRadius: 1.5,
                                    overflow: 'hidden',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5, flexShrink: 0 }}>
                                    <Typography variant="subtitle2" sx={{ color: textColor, fontWeight: 600, fontSize: '0.8rem' }} noWrap>
                                        {_(msg`Crashes by`)}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                        <FormControl
                                            size="small"
                                            sx={{
                                                minWidth: 140,
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.08)',
                                                    fontSize: '0.8rem',
                                                    '& fieldset': { borderColor },
                                                },
                                                '& .MuiInputLabel-root': { color: textColor, fontSize: '0.8rem' },
                                            }}
                                        >
                                            <InputLabel>{_(msg`Dimension`)}</InputLabel>
                                            <Select
                                                value={selectedSecondIndex}
                                                label={_(msg`Dimension`)}
                                                onChange={(e) => setSelectedSecondIndex(Number(e.target.value))}
                                            >
                                                {secondChartOptions.map((opt, i) => (
                                                    <MenuItem key={i} value={i} sx={{ fontSize: '0.8rem' }}>{opt.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => secondChartOptions[selectedSecondIndex] && onSecondChartSearch?.(secondChartOptions[selectedSecondIndex])}
                                            sx={{ background: theme.palette.newColors.cyanGradient, color: 'white', fontWeight: 700, borderRadius: 2, fontSize: '0.8rem' }}
                                        >
                                            {_(msg`Search`)}
                                        </Button>
                                    </Box>
                                </Box>
                                <Box sx={{ flex: 1, minHeight: 280, overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    {!secondChartData?.length ? (
                                        <Typography variant="body2" sx={{ color: textColor, fontStyle: 'italic', fontSize: '0.8rem', textAlign: 'center', px: 1 }}>
                                            {_(msg`Select a dimension and click Search`)}
                                        </Typography>
                                    ) : (
                                        <Box sx={{ flex: 1, minHeight: 280, width: '100%', display: 'flex', flexDirection: 'column' }}>
                                            <EChart chart={secondChartConfig} index={1} handleCharts={onChartTypeChange} width="100%" height="100%" areasGeoJson={null} />
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                        </>
                    )}
                </Box>
            </Box>
        </Drawer>
    )
}
